import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'

import s from './styles.module.scss'

import ProposalProgressBarStep from './proposal-progress-bar-step'
import {
  ClientProposalFragment,
  ProposalProgressBarStepDataFragment,
  TravelTypeEnum,
} from '../../../../lib/graphql'
import useDateFormatter from '../../../../lib/hooks/useDateFormatter'
import { DateFormatter } from '../../../../utils/DateFormatter'
import { UserTypeContextEnum, useUserTypeContext } from '../../../contexts'

export interface IProposalProgressBarInteractionsProps {
  isLoading?: boolean
}

interface IProposalProgressBarProps
  extends IProposalProgressBarInteractionsProps {
  stepData: ProposalProgressBarStepDataFragment
  clientProposal: null | Pick<ClientProposalFragment, 'type'>
}

const stepTitlesUser = (
  intl: IntlShape,
  clientProposal: IProposalProgressBarProps['clientProposal'],
) => [
  ...(clientProposal?.type === TravelTypeEnum.CORPORATE_EVENING_EVENT
    ? [
        intl.formatMessage({
          id: 'ui.stepTitlesUser.chooseEvent',
          defaultMessage: "Choisir l'événement",
        }),
      ]
    : [
        intl.formatMessage({
          id: 'ui.stepTitlesUser.chooseStay',
          defaultMessage: 'Choisir le séjour',
        }),
      ]),
  intl.formatMessage({
    id: 'ui.stepTitlesUser.confirmBooking',
    defaultMessage: 'Confirmer la réservation',
  }),
  intl.formatMessage({
    id: 'ui.stepTitlesUser.payDeposit',
    defaultMessage: "Régler l'acompte",
  }),
  intl.formatMessage({
    id: 'ui.stepTitlesUser.adjustServices',
    defaultMessage: 'Ajuster les prestations',
  }),
  intl.formatMessage({
    id: 'ui.stepTitlesUser.payBalance',
    defaultMessage: 'Régler le solde',
  }),
  ...(clientProposal?.type === TravelTypeEnum.CORPORATE_EVENING_EVENT
    ? [
        intl.formatMessage({
          id: 'ui.stepTitlesUser.dateOfTheEvent',
          defaultMessage: "Date de l'événement",
        }),
        intl.formatMessage({
          id: 'ui.stepTitlesUser.payBalancePostBookingEvent',
          defaultMessage: 'Solde post événement',
        }),
      ]
    : [
        intl.formatMessage({
          id: 'ui.stepTitlesUser.goOnRetreat',
          defaultMessage: 'Partir en séminaire',
        }),
        intl.formatMessage({
          id: 'ui.stepTitlesUser.payBalancePostBooking',
          defaultMessage: 'Solde post séjour',
        }),
      ]),
]

const stepTitlesOwner = (
  intl: IntlShape,
  clientProposal: IProposalProgressBarProps['clientProposal'],
) => [
  intl.formatMessage({
    id: 'ui.stepTitlesOwner.confirmBooking',
    defaultMessage: 'Confirmer la réservation',
  }),
  intl.formatMessage({
    id: 'ui.stepTitlesOwner.confirmAdjustments',
    defaultMessage: 'Confirmer les ajustements',
  }),
  intl.formatMessage({
    id: 'ui.stepTitlesOwner.welcomeClient',
    defaultMessage: 'Accueillir le client',
  }),
  ...(clientProposal?.type === TravelTypeEnum.CORPORATE_EVENING_EVENT
    ? [
        intl.formatMessage({
          id: 'ui.stepTitlesOwner.postEventBalance',
          defaultMessage: "Solde fin d'événement",
        }),
      ]
    : [
        intl.formatMessage({
          id: 'ui.stepTitlesOwner.postStayBalance',
          defaultMessage: 'Solde fin de séjour',
        }),
      ]),
]

const getStepTitles = ({
  userType,
  intl,
  clientProposal,
}: {
  userType: UserTypeContextEnum
  intl: IntlShape
  clientProposal: IProposalProgressBarProps['clientProposal']
}) => {
  switch (userType) {
    case UserTypeContextEnum.OWNER:
      return stepTitlesOwner(intl, clientProposal)

    case UserTypeContextEnum.USER:
      return stepTitlesUser(intl, clientProposal)
    default:
      return []
  }
}

const getStepDateText = ({
  dateFormatter,
  stepDataStepNumber,
  stepDate,
  stepNumber,
  intl,
}: {
  stepDate: Date
  stepNumber: number
  stepDataStepNumber: number
  dateFormatter: DateFormatter
  intl: IntlShape
}) => {
  if (stepNumber === stepDataStepNumber) {
    return intl.formatMessage({
      id: 'ui.getStepDateText.inProgress',
      defaultMessage: 'En cours',
    })
  } else if (stepDate && stepNumber <= stepDataStepNumber) {
    return dateFormatter.format({ date: stepDate, formatString: 'DD/MM/YYYY' })
  }
  return ''
}

const ProposalProgressBar = ({
  stepData,
  clientProposal,
}: IProposalProgressBarProps) => {
  const intl = useIntl()
  const dateFormatter = useDateFormatter()
  const { userType } = useUserTypeContext()

  const [progressWidth, setProgressWidth] = useState<number>(0)
  const progressBarRef = useRef<HTMLDivElement | null>(null)

  const stepTitles = getStepTitles({ userType, intl, clientProposal }) ?? []

  const nbStepsMax = stepTitles.length

  const calculateProgressWidth = (newStep: number) =>
    ((newStep - 1) / (nbStepsMax - 1)) * 100

  useEffect(() => {
    setProgressWidth(calculateProgressWidth(stepData.stepNumber))
  }, [stepData.stepNumber])

  return (
    <div ref={progressBarRef} className={s.progressBarContainer}>
      <div className={clsx(s.progressBar, s.progressBarWithoutDate)}>
        <span className={s.progress} style={{ width: `${progressWidth}%` }} />
        {stepTitles.map((title, idx) => {
          const stepNumber = idx + 1
          const stepDate = (stepData.stepsUpdatedAt || []).find(
            (step) => step.stepNumber === stepNumber,
          )?.date
          const stepDateText = getStepDateText({
            stepDate,
            stepNumber,
            stepDataStepNumber: stepData.stepNumber,
            dateFormatter,
            intl,
          })

          return (
            <ProposalProgressBarStep
              key={title}
              title={title}
              step={stepNumber}
              activeStep={stepData.stepNumber}
              stepDate={stepDateText}
            />
          )
        })}
      </div>
    </div>
  )
}

export default ProposalProgressBar
